import {
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarDocument,
  GetUserPreferencesForUnitSelectionDocument,
  UnitSystemEnum,
  useGenerateReportPdfMutation,
  useUpdatePreferencesMutation,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { abyssColors } from '@/theme/colors';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { PictureAsPdf } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { ConfirmUnitSystemDialog } from './ConfirmUnitSystemDialog';

export const ExportPoiReportButton = () => {
  const [selectedReport] = useAtom(state.selectedReport);
  const [unitSystem, setSelectedUnit] = useAtom(state.unitSystem);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isAITEngineer = useFeatureFlag('ait-engineer');

  const [generateReportPdf] = useGenerateReportPdfMutation();
  const [updatePreferencesMutation] = useUpdatePreferencesMutation();
  const { showSnackBar } = useSnackBarMessage({ isReportDownload: true });

  const handlePointOfInterestPdf = async () => {
    if (!selectedReport?.id) {
      throw new Error('Report ID is missing');
    }

    const { data } = await generateReportPdf({
      variables: { reportIds: [selectedReport.id] },
    });

    if (!data?.generateReportPdf?.downloadUrl) {
      throw new Error('Failed to generate PDF');
    }

    showSnackBar('Your reports are ready. Download is starting...', 'success');

    // Download from URL
    if (data.generateReportPdf.downloadUrl && data.generateReportPdf.downloadUrl.length > 0) {
      const downloadUrl = data.generateReportPdf.downloadUrl.at(0);
      if (downloadUrl) {
        // Add a delay to ensure the second snackbar is visible before starting download
        setTimeout(() => {
          // Open the download URL directly - the backend should set the correct filename
          window.open(downloadUrl, '_blank');

          // Show final success message after download
          setTimeout(() => {
            showSnackBar('Your reports have been downloaded successfully', 'success');
          }, 500);
        }, 2000);
      }
    }
  };

  const runExport = async () => {
    showSnackBar(
      'Preparing your reports. Download will start once ready, you can continue working.',
      'info',
      { persistent: true }
    );
    await handlePointOfInterestPdf();
  };

  const handleDownloadPDF = async () => {
    if (loading) return;

    try {
      setLoading(true);
      await runExport();
    } catch (error) {
      showSnackBar('Download failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAndExport = async (pendingUnit: UnitSystemEnum) => {
    if (loading) return;
    setDialogOpen(false);

    try {
      setLoading(true);

      if (pendingUnit !== unitSystem) {
        const response = await updatePreferencesMutation({
          variables: { input: { unit: pendingUnit } },
          refetchQueries: [
            GetUserPreferencesForUnitSelectionDocument,
            GetAssembliesByTemplateFieldsFiltersForFilterSideBarDocument,
          ],
        });
        const updatedUnit = response.data?.updateUserPreferences?.preferences?.unit;
        if (!updatedUnit) {
          showSnackBar('Failed to update unit system. Please try again.', 'error');
          return;
        }
        setSelectedUnit(pendingUnit);
      }

      await runExport();
    } catch (error) {
      showSnackBar('Download failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleIconClick = () => {
    if (loading) return;
    if (isAITEngineer) {
      setDialogOpen(true);
      return;
    }
    handleDownloadPDF();
  };

  return (
    <>
      {loading ? (
        <CircularProgress
          sx={{ color: abyssColors.primary[500] }}
          size={16}
          data-testid="generate-pdf-loading"
        />
      ) : (
        <PictureAsPdf
          data-testid="export-report-icon"
          htmlColor={abyssColors.primary[500]}
          sx={{ cursor: 'pointer' }}
          onClick={handleIconClick}
        />
      )}
      <ConfirmUnitSystemDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmAndExport}
      />
    </>
  );
};
